import { useStore } from '@nanostores/react';
import { accountStore, authStore } from './store';
import type { AugmentedAccount } from './types';

export const AccountFeatureFlag = {
  DeveloperToolsPanel: 'developer-tools-panel',
  WebPush: 'web-push',
  WebPushAlerts: 'web-push-alerts',
  PostmasterTools: 'postmaster-tools',
  DedicatedIpHealth: 'dedicated-ip-health',
  Whatsapp: 'whatsapp',
  StoreAnonEvents: 'store_anon_events',
  JavascriptWebhook: 'javascript_webhook',
  LegacyCustomSmtpBilling: 'legacy_custom_smtp_billing',
  MultiLanguageSettings: 'multi_language_settings',
  ExitOnlyAfterMessage: 'exit_only_after_message',
  ParcelAmp: 'parcel_amp',
  CdpDataCatalogEnabled: 'cdp_data_catalog_enabled',
  VirtualSupportChatEnabled: 'virtual_support_chat_enabled',
  InAppLegacyEditor: 'in_app_legacy_editor',
  FlagCDPUIConsolidation: 'cdp_ui_consolidation',

  ShowGoals: 'show-goals',
  GoalsMilestones: 'goals-milestones',
  GoalsOnboardingRecommendations: 'goals-onboarding-recommendations',
  GoalsEmbed: 'goals-embed',
  GoalsToConversions: 'goals-to-conversions',
  OutcomeOptimization: 'outcome_optimization',
  McpExternalAccountToggle: 'mcp-external-account-toggle',
  TasksPerformanceDashboard: 'tasks_performance_dashboard_enabled',
  QuietHours: 'quiet-hours',
  // Pulled from Ember feature flags
  BranchingAnyObjectCondition: 'branching_any_object_condition',
  StateBasedObjectCampaignTrigger: 'state-based-object-campaign-trigger',
  GoalsCreatePreview: 'goals-create-preview',
  AiAutoSegmentNames: 'ai_auto_segment_names',
  DeliverabilityAnalytics: 'deliverability-analytics',
  TransactionalSms: 'transactional-sms',
  SmsByoForNative: 'sms-byo-for-native',
  SmsGeoSenders: 'sms-geo-senders',
  ContentFirstInBroadcast: 'content-first-in-broadcast',
  NewsletterRateLimitWithTimezone: 'newsletter-rate-limit-timezone',
  NewsletterSendDeadline: 'newsletter_send_deadline',
  NewsletterRecipientsUpload: 'newsletter-recipients-upload',
  StoForNewsletters: 'sto_for_newsletters',
  InAppEditorLeadForm: 'in-app-editor-lead-form',
  DefaultFromIdentity: 'default-from-identity',
  EmailAutoTls: 'email-auto-tls',
  ConfiguredWebhooks: 'configured_webhooks',
  LLMActionBYOK: 'llm-action-byok',
  LLMActionGuided: 'llm-action-guided',
  InAppForms: 'in-app-forms',
  SignUpForms: 'sign-up-forms',
  TeamMembersExport: 'team_member_export',
  ObjectWaitUntil: 'obj_wait_until',
  JourneyAttributes: 'journey-attributes',
  WhatsappNativeFreeText: 'whatsapp-native-free-text',
  ShowTemplatesSnippetsLayouts: 'show_templates_snippets_layouts',
  InAppGlobalStyles: 'in-app_global-styles',
  HalloweenStickies2025: 'halloween_stickies_2025',
  DesignDefenderGame: 'design-defender-game',
  OptimizedMessageLibrary: 'optimized-message-library',

  DsSocialIcons: 'ds-social-icons',
  DsCountdown: 'ds-countdown',
  DsVideo: 'ds-video',
  DsUiPlayground: 'ds-ui-playground',
  ImproveDsAgent: 'improve-ds-agent',
  AgentFirstEmails: 'agent-first-emails',
  ClassicEditorsSunset: 'classic-editors-sunset',
  ClassicContentUpgrade: 'classic-content-upgrade',

  InAppTooltips: 'in-app-tooltips',
  InAppLivePreview: 'in-app_live-preview',
  InAppDesignStudio: 'in-app_design-studio',
  PushDesignStudio: 'push_design-studio',
  DsInfiniteCanvas: 'ds-infinite-canvas',
  DsUseTiptap: 'ds-use-tiptap',
  DsFixHighlight: 'ds-fix-highlight',
  DsHtmlToCarta: 'ds-html-to-carta',
  DsMargins: 'ds-margins',
  DsMultiLanguageCanvas: 'ds-multi-language-canvas',
  DsProcessWorker: 'ds-process-worker',
  DsRenderDependencies: 'ds-render-dependencies',
  DsReviewPanelImprovements: 'ds-review-panel-improvements',
  DsPropertiesLiquidAutocomplete: 'ds-properties-liquid-autocomplete',
  SampleDataSegmentFilter: 'sample-data-segment-filter',
  DsPreviewLanguageMatch: 'ds-preview-language-match',
  PausePlanM2: 'pause-plan-m2',
  PavedRoad: 'paved_road',

  MultipleShortlinkDomains: 'multiple_shortlink_domains',
  NotificationsUx: 'notifications-ux',
  TestDeliveryMode: 'test_delivery_mode',
  TemplateEngineSwitch: 'template_engine_switch',
  AccountVerification: 'account-verification',
  PushV2: 'push_v2',
  MultiApp: 'multi_app',
  DsLiquidExpressions: 'ds-liquid-expressions',
  Gravatar: 'gravatar',
  GravatarEnabled: 'gravatar_enabled',
  AllowAgentEditLiveData: 'allow_agent_edit_live_data',
  AiEndpointLocked: 'ai_endpoint_locked',
  McpAllowEditLiveData: 'mcp_allow_edit_live_data',
  McpAllowAccessSensitiveData: 'mcp_allow_access_sensitive_data',
  AgentOpusModel: 'agent-opus-model',
  AgentQueryBuilder: 'agent_query_builder',
  CdpSalesforceEcaMigration: 'cdp_salesforce_eca_migration',
  CdpSalesforceEcaMigrationOptout: 'cdp_salesforce_eca_migration_optout',
  CdpTrackEnabled: 'cdp_track_enabled',
  CdpPassthroughEnabled: 'cdp_passthrough_enabled',
  WhiteLabelTwilio: 'white_label_twilio',
  WhiteLabelTwilioOnboarding: 'white_label_twilio_onboarding',
  CampaignDraftMode: 'campaign-draft-mode',
  CampaignDraftLiveEdits: 'campaign-draft-live-edits',
  SeedLists: 'seed-lists',
  EditorBreadcrumbs: 'editor-breadcrumbs',
  DsInsertMenuPreviews: 'ds-insert-menu-previews',
  DsAssetMoveTo: 'ds-asset-move-to',
  DsInboxLegacyBaseVariant: 'ds-inbox-legacy-base-variant',
  NewPipelinesObjectsPricing: 'new_pipelines_objects_pricing',
  NewPipelinesObjectsBilling: 'new_pipelines_objects_billing',
  BuilderOfferEnabled: 'builder_offer_enabled',
  GenericSamlSso: 'generic_saml_sso',
  OrdwayCouponCodes: 'ordway_coupon_codes',
  LegacyTwilio: 'legacy-twilio',
  DryadConditionPicker: 'dryad-condition-picker',
  InAppToursAction: 'in-app_tours_action',
  EssentialsOnboarding: 'essentials_onboarding',
  OnboardingBrandPreview: 'onboarding_brand_preview',
  CdpJourneysSqlImportAvailable: 'cdp_journeys_sql_import_available',
  ReverseEtlConsolidation: 'reverse_etl_consolidation',
  DashboardEditorPoc: 'dashboard_editor_poc',
  WorkflowBuilderMigration: 'workflow-builder-migration',
  ComposerMigration: 'composer-migration',
  ComposerCreateOrUpdatePerson: 'composer-create-or-update-person',
  ComposerJourneyAttrUpdate: 'composer-journey-attr-update',
  ComposerCreateEvent: 'composer-create-event',
  ComposerBatchUpdate: 'composer-batch-update',
  ComposerQueryObjects: 'composer-query-objects',
  ComposerCollectionQuery: 'composer-collection-query',
  ComposerSlack: 'composer-slack',
  ComposerUrbanAirship: 'composer-urban-airship',
  ComposerLine: 'composer-line',
  ObjectSegmentCounts: 'object-segment-counts',
  CampaignsListMigration: 'campaigns-list-migration',
  SegmentImportsMigration: 'segment-imports-migration',
  AutomationDraftsMigration: 'automation-drafts-migration',
  CampaignSentTabMigration: 'campaign-sent-tab-migration',
  CampaignFormSubmissionsTabMigration: 'campaign-form-submissions-tab-migration',
  CampaignJourneysTabMigration: 'campaign-journeys-tab-migration',
  CampaignTestsTabMigration: 'campaign-tests-tab-migration',
  CampaignOverviewTabMigration: 'campaign-overview-tab-migration',
  TransactionalSentTabMigration: 'transactional-sent-tab-migration',
  TransactionalDraftsTabMigration: 'transactional-drafts-tab-migration',
  TransactionalSendTabMigration: 'transactional-send-tab-migration',
  TransactionalMetricsTabMigration: 'transactional-metrics-tab-migration',
  TransactionalSetupMigration: 'transactional-setup-migration',
  SimplifiedTransactional: 'simplified-transactional',
  PauseResumeNewsletters: 'pause_resume_newsletters',
  TransactionalApiListMigration: 'transactional-api-list-migration',
  TwoFactorSetupMigration: 'two-factor-setup-migration',
  SegmentsListMigration: 'segments-list-migration',
  SegmentUsageMigration: 'segment-usage-migration',
  DraftDetailMigration: 'draft-detail-migration',
  SegmentAudiencesMigration: 'segment-audiences-migration',
  SegmentManualOverviewMigration: 'segment-manual-overview-migration',
  GoalsMigration: 'goals-migration',
  ExperimentAction: 'experiment-action',
  QueryObjectsAction: 'query-objects-action',
  ObjectCampaignBackfill: 'object_campaign_backfill',
  ContentPreviewOptimization: 'content_preview_optimization',
  SqlSegments: 'sql_segments',
  SnapshotSegments: 'snapshot_segments',
  HalloweenTheme: 'halloween-theme-2026',
  UnifiedConditionFormatPeople: 'unified-condition-format-people',
} as const;

export type AccountFeatureFlag = (typeof AccountFeatureFlag)[keyof typeof AccountFeatureFlag];

export type FeatureFlag = AccountFeatureFlag;
export const FeatureFlag = { ...AccountFeatureFlag };

export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = Object.freeze({
  [AccountFeatureFlag.DeveloperToolsPanel]: false,
  [AccountFeatureFlag.WebPush]: false,
  [AccountFeatureFlag.WebPushAlerts]: false, // staff-only experimental browser alerts about your own jobs; resolved for the logged-in user, never on by default
  [AccountFeatureFlag.PostmasterTools]: false, // Settings → Email → Postmaster Tools and Reports → Postmaster Tools; internal-only while it is tested
  [AccountFeatureFlag.DedicatedIpHealth]: false, // Sending domain → Mail servers dedicated IP warm-up and health; internal-only while it is tested
  outcome_optimization: false, // internal prototype: agent-driven goal A/B testing
  whatsapp: false, // whether to enable sending to whatsapp numbers in Twilio SMS action
  store_anon_events: true, // enables the collection of anonymous events.
  javascript_webhook: false, // enables adding javascript reporting webhook
  legacy_custom_smtp_billing: true, // whether to skip message overages for accounts with custom smtp https://github.com/customerio/issues/5947
  multi_language_settings: false, // enables custom multi-language settings
  exit_only_after_message: false, // enables the "exit only after receiving a message" exit option for campaigns
  parcel_amp: true, // enables AMP in Parcel
  cdp_data_catalog_enabled: false, // enabled Data Catalog menu item and page in CDP
  virtual_support_chat_enabled: false, // enables the virtual support chat via Zendesk widget
  in_app_legacy_editor: false, // enables the legacy in-app editor
  cdp_ui_consolidation: false, //if enabled, CDP is now presented inside the "Integrations" and no sidebar is displayed

  'show-goals': false, // will gate the goals surfaces (route, sidebar link, command bar, automations/broadcasts filters, home prompts); consumers land in follow-ups (FLO-4772). Keep the default false: the account payload carries only enabled flags, so an absent key has to read as off
  'goals-milestones': false, // enables the goals milestones feature
  'goals-onboarding-recommendations': false, // enables the recommendations-based goals empty state, built from the workspace's own converting conditions (FLO-4432)
  'goals-embed': false, // enables the goal-attachments-editor section (attach an existing goal) on automations, FLO-4339
  'goals-to-conversions': false, // enables the Conversions tab on the data index, which reads the workspace's goals as conversions (AUD-4578)
  'mcp-external-account-toggle': false, // mcp external account toggle
  tasks_performance_dashboard_enabled: false, // enables the tasks performance dashboard
  'quiet-hours': false, // gives an account access to SMS quiet hours
  // Ember feature flags
  branching_any_object_condition: true, // on for every account by default; offers the attribute-source select on a condition reading the current person's relationship to an object
  'state-based-object-campaign-trigger': false, // lets the account save the consolidated Object (rel_seg_attr) campaign trigger; the trigger picker offers it only with this on
  'goals-create-preview': false, // enables the preview-first goal create page in place of the stepper wizard
  ai_auto_segment_names: false, // enables the AI auto segment names feature
  'deliverability-analytics': false, // enables the deliverability analytics feature
  'transactional-sms': false, // enables the transactional SMS feature
  'sms-byo-for-native': false, // lets a Customer.io-managed SMS workspace connect its own providers too
  'sms-geo-senders': false, // dynamic senders in SMS settings (MESS-1637) and the vendor error line on failed SMS deliveries (MESS-1591)
  'content-first-in-broadcast': false, // enables content and channel selection before recipients in broadcasts
  'newsletter-rate-limit-timezone': false, // enables the newsletter rate limit timezone feature
  newsletter_send_deadline: false,
  'newsletter-recipients-upload': false,
  sto_for_newsletters: false, // offers Send time optimization on an email one-time send's Schedule step
  'in-app-editor-lead-form': false, // enables the in-app editor lead form feature
  'default-from-identity': false, // enables the default from identity feature
  'email-auto-tls': false, // enables the email auto TLS feature
  configured_webhooks: false, // enables the configured webhooks feature
  'llm-action-byok': false, // enables the LLM action BYOK feature
  'llm-action-guided': false, // enables generating an LLM action from a description
  'in-app-forms': false, // enables the in-app forms feature
  'sign-up-forms': false, // enables the sign-up forms page
  team_member_export: false, // enables the team member export feature
  obj_wait_until: false, // enables the obj wait until feature
  'journey-attributes': false, // enables the journey attributes feature
  'whatsapp-native-free-text': false, // enables the WhatsApp native free text feature
  show_templates_snippets_layouts: false, // enables the show templates snippets layouts feature
  'in-app_global-styles': false, // enables the in-app global styles feature
  halloween_stickies_2025: false, // enables the Halloween stickies 2025 feature
  'optimized-message-library': false, // enables the optimized message library feature
  'ds-social-icons': false, // enables the social icons component feature
  'ds-countdown': false, // enables the countdown component feature
  'ds-video': false, // enables the video component feature
  'ds-ui-playground': false, // enables the design studio UI playground
  'improve-ds-agent': false, // consolidated Design Studio agent-improvements flag: the brand design system document (its Brand section on the Global Styles page), distilled org patterns, and the inline AI edit quality tier
  'agent-first-emails': false, // agent-first email creation: the DS create-email starting-point flow (CON-8087); the ui repo gates its automation, broadcast and transactional starters on the same key
  'classic-editors-sunset': false,
  'classic-content-upgrade': false, // gates the classic content upgrade path: conversion entry points, classic emails view and nudges (P-CON-1990)

  'in-app-tooltips': false, // enables the in-app tooltips feature
  'in-app_live-preview': false, // enables the in-app live preview feature
  'in-app_design-studio': false, // rollout flag for authoring in-app messages as Design Studio nodes
  'push_design-studio': false, // rollout flag for authoring push messages as Design Studio nodes
  'ds-infinite-canvas': false, // enables the infinite canvas visual editor in Design Studio
  'ds-use-tiptap': false, // enables the tiptap-based visual editor in Design Studio
  'ds-fix-highlight': false, // highlights what the agent's fix_template changed: pending shimmer while it works, applied ring + code-view reveal when it lands
  'ds-html-to-carta': false, // offers to convert a raw-HTML email into Carta from the editor
  'ds-margins': false, // on-canvas margin grips (top/bottom) for structure components
  'ds-multi-language-canvas': false, // multi-email canvas: every variant of a language group renders as an artboard; requires ds-infinite-canvas
  'ds-process-worker': false, // shadow mode: preview renders also run in a web worker and differences are reported to Sentry
  'ds-render-dependencies': false, // asks /process to report the workspace inputs each render read
  'ds-review-panel-improvements': false, // gates the customer-visible Review panel changes (readiness score gauge card)
  'ds-properties-liquid-autocomplete': false, // `{{` snippet/variable suggestions in Design Studio properties-panel text and URL fields
  'sample-data-segment-filter': false, // "Segment" option in the Design Studio personalization panel's sample-data profile search
  'ds-preview-language-match': false, // says which language version the "Previewing as" person gets, and opens it when you pick a person
  'pause-plan-m2': false, // enables the real implementation of account pausing
  paved_road: false, // enables the CDP paved road onboarding experience
  multiple_shortlink_domains: false, // enables the multiple shortlink domains feature
  'notifications-ux': false, // enables the new notifications drawer UX
  test_delivery_mode: true,
  template_engine_switch: false, // lets a workspace switch its default template engine (liquid version) even without can_update_default_template_engine
  'account-verification': false,
  push_v2: false, // Delivery-per-device push sending
  multi_app: false, // enables multiple mobile apps for push
  'ds-liquid-expressions': false, // enables liquid conditional logic in Design Studio
  gravatar: false, // enables the gravatar feature
  gravatar_enabled: false, // whether external avatars (e.g. Gravatar) are fetched for the account
  allow_agent_edit_live_data: false, // default off — admins opt in from Privacy & AI to let AI agents edit live data
  ai_endpoint_locked: false, // support-set in Flightdeck: freezes the EU/Global AI endpoint so only support can change it
  mcp_allow_edit_live_data: false, // off by default; self-serve signups get it turned on at account creation
  mcp_allow_access_sensitive_data: false, // off by default — admins opt in from AI & MCP settings
  'agent-opus-model': false, // lets the account choose which model the agent runs on
  agent_query_builder: false, // analytics answers carry a "View and edit this query" link that opens the visual query builder

  cdp_salesforce_eca_migration: false,
  cdp_salesforce_eca_migration_optout: false, // when on, treats cdp_salesforce_eca_migration as off (lets accounts opt out of the ECA migration)
  cdp_track_enabled: false,
  cdp_passthrough_enabled: false, // routes Journeys Track-API data through Pipelines sources (with cdp_track_enabled, gates auto-track sources)
  white_label_twilio: false, // white-labelled Twilio integration
  white_label_twilio_onboarding: false, // SMS registration onboarding; when off, the SMS settings page redirects to the Twilio settings page
  'campaign-draft-mode': false, // enables draft mode for automations: editing a live campaign as a draft that publishes atomically
  'campaign-draft-live-edits': false, // with campaign-draft-mode, still lets a started automation's live messages be edited and published directly; off, they change only through a draft
  'seed-lists': false, // seed lists: workspace settings for lists of team members, sending to them, and the send log
  'editor-breadcrumbs': false, // enables Hydra breadcrumbs on editor/composer pages
  'ds-insert-menu-previews': false, // enables insert menu previews in Design Studio
  'ds-asset-move-to': false, // adds "Move to…" (per item and for selected items) to the asset library
  'ds-inbox-legacy-base-variant': false, // temporary: keeps the inbox preview's CTA on the button variant whose name sorts first, matching what services still sends these accounts' inboxes
  new_pipelines_objects_pricing: false, // enables the "new pipelines objects pricing" plan highlights
  new_pipelines_objects_billing: false, // combines person + object profile overages into one line on billing-history invoices
  builder_offer_enabled: false, // offers a Builder downgrade in the cancel flow and on the account-canceled page. Builder is paused (SPE-4075), so the offer stays hidden until Flightdeck turns this on.
  generic_saml_sso: false, // enables the Generic SAML 2.0 identity provider option on the SSO settings page
  ordway_coupon_codes: false, // enables the Ordway coupon/draft-subscription flow on the checkout page
  'legacy-twilio': false, // enables the original Twilio integration
  'dryad-condition-picker': false, // replaces the people filter's add-condition and row-type selects with the faceted condition picker; same key as Ember's flag
  'in-app_tours_action': false, // offers the Product Tour tile in the workflow builder's Add panel
  essentials_onboarding: false, // Essentials-trial guided onboarding takeover; the cohort is trial + non-builder plan + this flag (see features/onboarding/completion.ts)
  onboarding_brand_preview: false, // shows the brand email preview beside the onboarding wizard; the brand_preview route it reads answers 403 while this is off
  cdp_journeys_sql_import_available: false, // shows SQL Import integrations in the directory even when no import is set up
  reverse_etl_consolidation: false, // the SQL-sync pages forward to Pipelines Reverse ETL, which has replaced them
  dashboard_editor_poc: false, // proof-of-concept Boards pages (/workspaces/:id/boards)
  'workflow-builder-migration': false, // rollout flag: serves the automation workflow builder (canvas) from Hydra instead of the embedded Ember app (FPS-2338)
  'composer-migration': false, // area flag: gives the action composer URLs to Hydra; the per-type composer-* flags below choose which actions Hydra composes, and every other action stays with the embedded Ember app (FPS-4673)
  'composer-create-or-update-person': false, // rollout flag: Hydra composes "create or update person" actions while composer-migration is on (FPS-4673)
  'composer-journey-attr-update': false, // rollout flag: Hydra composes "set journey attributes" actions while composer-migration is on (FPS-5101)
  'composer-create-event': false, // rollout flag: Hydra composes "send event" actions while composer-migration is on (FPS-5126)
  'composer-batch-update': false, // rollout flag: Hydra composes "batch update" actions while composer-migration is on (FPS-5144)
  'composer-query-objects': false, // rollout flag: Hydra composes "query objects" actions while composer-migration is on (FPS-5149)
  'composer-collection-query': false, // rollout flag: Hydra composes "query collection" actions while composer-migration is on (FPS-5149)
  'composer-slack': false, // rollout flag: Hydra composes Slack actions while composer-migration is on (FPS-5258)
  'composer-urban-airship': false, // rollout flag: Hydra composes Urban Airship actions while composer-migration is on (FPS-5310)
  'composer-line': false, // rollout flag: Hydra composes LINE actions while composer-migration is on (FPS-5310)
  'object-segment-counts': false, // offers a segment condition on the objects list, narrowing to the objects related to the segment's people
  'segment-imports-migration': false, // rollout flag: lets segment viewers into a segment's CSV import in the Hydra imports wizard and hands its /journeys/segments/:id/imports/csv URLs off to it instead of the embedded Ember app (FPS-4216)
  'automation-drafts-migration': false, // rollout flag: serves an automation's Drafts tab from Hydra (FPS-4189)
  'campaign-sent-tab-migration': false, // rollout flag: serves an automation's Sent tab list and its delivery detail from Hydra
  'campaign-form-submissions-tab-migration': false, // rollout flag: serves an automation's Form Submissions tab from Hydra (FPS-4266)
  'campaign-journeys-tab-migration': false, // rollout flag: serves an automation's Journeys tab (FPS-4326) and its webhook journey detail (FPS-4325) from Hydra
  'campaign-tests-tab-migration': false, // rollout flag: serves an automation's legacy A/B Tests tab list from Hydra when experiments aren't on offer (FPS-4267), and one A/B test's results whatever experiments say (FPS-4323)
  'campaign-overview-tab-migration': false, // rollout flag: serves an automation's Overview tab, and its bare address, from Hydra (FPS-4264)
  'transactional-sent-tab-migration': false, // rollout flag: serves a transactional message's Sent tab and its delivery detail from Hydra (FPS-4333)
  'transactional-drafts-tab-migration': false, // rollout flag: serves a transactional message's Drafts tab from Hydra (FPS-4342)
  'transactional-send-tab-migration': false, // rollout flag: serves a Builder transactional message's Send tab from Hydra (FPS-4340)
  'transactional-metrics-tab-migration': false, // rollout flag: serves a transactional message's Metrics tab from Hydra (FPS-4331)
  'transactional-setup-migration': false, // rollout flag: serves a transactional message's setup wizard (/transactional/:id/setup) from Hydra (FPS-4334)
  'simplified-transactional': false, // feature flag, shared with Ember: gives Flex and Essentials accounts Builder's transactional flow (TAG-2494)
  'campaigns-list-migration': false, // rollout flag: serves the Automations list (/automations) from Hydra instead of the embedded Ember app (FPS-4194)
  pause_resume_newsletters: false, // Ember's one-time send pause and resume; here it only renames a stopped send "Canceled" on the Broadcasts list
  'two-factor-setup-migration': false, // rollout flag: serves the forced 2FA enrollment wizard (/2fa_setup) from Hydra instead of the embedded app (FPS-4244)
  'transactional-api-list-migration': false, // rollout flag: serves the Transactional list (/transactional) from Hydra for every plan but Builder, the "Transactional API" cards (FPS-4328)
  'segments-list-migration': false, // rollout flag: serves the Segments list from Hydra instead of the embedded app
  'segment-usage-migration': false, // rollout flag: serves a segment's Usage tab (and the segment detail frame around it) from Hydra (FPS-4224)
  'draft-detail-migration': false, // rollout flag: serves the draft detail and preview from Hydra; the draft editor stays embedded
  'segment-audiences-migration': false, // rollout flag: serves a segment's Ad Audiences tab from Hydra (FPS-4299)
  'segment-manual-overview-migration': false, // rollout flag: serves a manual segment's Overview and People tabs from Hydra; condition-based segments stay embedded (FPS-2672)
  'goals-migration': false, // rollout flag: serves the Goals pages from Hydra; they also need show-goals, which hides goals entirely when off (FPS-2328)
  'experiment-action': false, // gates the Experimentation hub and the in-automation Experiment action
  'query-objects-action': false, // gates the Query Objects workflow step: its add-actions tile and its failure's Activity Type filter option. The backend refuses to create the step without it
  'three-panel-delay-chart': false, // splits workspace health delay chart into three separate panels
  object_campaign_backfill: false, // offers the backfill choice when an object or relationship automation starts or is scheduled. An account flag set in services, not FlightDeck
  'design-defender-game': false, // shows the Design Defender mini game in the home sidebar
  content_preview_optimization: false, // cuts a workflow email's Content preview to its first 30,000 characters so a large email does not stall the panel (FLO-2072)
  sql_segments: false, // an AI feature: offers SQL segments, which also need ai_enabled
  snapshot_segments: false, // offers snapshot segments, a query answered once until refreshed
  'halloween-theme-2026': false, // feature flag: offers the Halloween dark theme in the person menu's theme selector
  'unified-condition-format-people': false, // validates People list, export and bulk delete filters against the people_searchable contract in services; an invalid filter gets a 422 with typed violations (FLO-4899)
});

const useFeatureFlags = () => {
  const account = useStore(accountStore);
  const flags = account?.feature_flags ?? {};

  return Object.assign({}, FEATURE_FLAG_DEFAULTS, flags);
};

export const useFeatureFlag = (flag: FeatureFlag | string) => {
  const flags = useFeatureFlags();
  return flags[flag] ?? false;
};

/**
 * Pure flag check for an explicit account. When the account is null (not
 * loaded yet), the flag's registered default applies — callers that render
 * before auth completes must treat that answer as provisional and re-evaluate
 * once the account arrives (subscribe to `accountStore`, or use a reactive
 * hook like `useFeatureFlag`).
 */
export const accountHasFeatureFlag = (
  account: Pick<AugmentedAccount, 'feature_flags'> | null | undefined,
  flag: FeatureFlag,
) => {
  const defaultValue = FEATURE_FLAG_DEFAULTS[flag] ?? false;

  if (account == null) {
    return defaultValue;
  }

  return account.feature_flags?.[flag] ?? defaultValue;
};

/**
 * Call-time snapshot of `accountHasFeatureFlag` against the current auth
 * store. Safe in event handlers and other post-auth code paths; do NOT use
 * during render for anything that gets cached — the pre-auth answer is the
 * flag default and won't recompute (see `useFeatureFlag`).
 */
export const hasFeatureFlag = (flag: FeatureFlag) => {
  return accountHasFeatureFlag(authStore.get()?.account, flag);
};
